import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetUserQuery } from 'src/components/User/GetUser.generated';
import { UserTypeEnum } from 'src/graphql/types.generated';
import { useDeveloperBypass } from './useDeveloperBypass';
import { useIneligibleByGroup } from './useIneligibleByGroup';
import { useReportsDisabled } from './useReportsDisabled';

export type NavItems = {
  id: string;
  title: string;
  subTitle?: string;
  hideItem?: boolean;
};

export function useReportNavItems(): NavItems[] {
  const { t } = useTranslation();
  const { data } = useGetUserQuery();
  const { reportsDisabled } = useReportsDisabled();
  const developerBypass = useDeveloperBypass();
  const { inStaffAccountIneligibleGroup } = useIneligibleByGroup();

  const userType = data?.user.userType;
  const usStaff = userType === UserTypeEnum.UsStaff;
  const hybridStaff = userType === UserTypeEnum.HybridStaff;

  const hasNoStaffAccount =
    data && typeof data.user.staffAccountId !== 'string';

  const hideReport =
    (!usStaff && !hybridStaff) ||
    hasNoStaffAccount ||
    inStaffAccountIneligibleGroup;

  const reportNavItems: NavItems[] = [
    {
      id: 'donations',
      title: t('Donations'),
    },
    {
      id: 'partnerCurrency',
      title: t('14 Month Partner Report'),
      subTitle: t('Partner Currency'),
    },
    {
      id: 'salaryCurrency',
      title: t('14 Month Salary Report'),
      subTitle: t('Salary Currency'),
    },
    ...(reportsDisabled
      ? []
      : [
          {
            id: 'staffExpense',
            title: t('Staff Expense Report'),
            hideItem: hideReport,
          },
          {
            id: 'mpgaIncomeExpenses',
            title: t('Income/Expense Analysis'),
            hideItem: hideReport,
          },
        ]),
    {
      id: 'designationAccounts',
      title: t('Designation Accounts'),
    },
    {
      id: 'financialAccounts',
      title: t('Responsibility Centers'),
    },
    {
      id: 'expectedMonthlyTotal',
      title: t('Expected Monthly Total'),
    },
    {
      id: 'partnerGivingAnalysis',
      title: t('Partner Giving Analysis'),
    },
    {
      id: 'coaching',
      title: t('Coaching'),
    },
  ];

  return useMemo(
    () => reportNavItems.filter((item) => developerBypass || !item.hideItem),
    [
      t,
      usStaff,
      hybridStaff,
      reportsDisabled,
      hasNoStaffAccount,
      inStaffAccountIneligibleGroup,
      developerBypass,
    ],
  );
}
